import { motion } from "framer-motion";
import { ArrowRight, XCircle, CheckCircle2 } from "lucide-react";
import carSilhouette from "@/assets/car-silhouette.webp";

const problems = [
  {
    problem: "Vehicles off-site for hours",
    solution: "We come to you",
    detail: "On-site service at your premises",
  },
  {
    problem: "Staff time wasted on car\u00A0runs",
    solution: "Zero staff involvement",
    detail: "Your team stays focused on work",
  },
  {
    problem: "Scheduling headaches",
    solution: "We handle everything",
    detail: "Recurring schedules, fully managed",
  },
];

export const ProblemSection = () => {
  return (
    <section className="section-muted py-20 md:py-28 relative overflow-hidden">
      {/* Car silhouette at top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl opacity-[0.08] pointer-events-none">
        <img 
          src={carSilhouette} 
          alt="" 
          className="w-full h-auto"
          aria-hidden="true"
        />
      </div>
      
      <div className="container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="eyebrow">Why corporates choose us</span>
          <h2 className="heading-section mt-3 mb-4">
            Spotless Vehicles.
            <br />
            <span className="text-accent">Zero Operational Friction.</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Drive-throughs cost your business hours every week. We bring the
            wash to you.
          </p>
        </motion.div>

        {/* Problem/Solution Grid */}
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 md:gap-7">
            {problems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.12, duration: 0.6, ease: "easeOut" }}
                whileHover={{ y: -6 }}
                className="group relative bg-card rounded-2xl overflow-hidden border border-border shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-elegant)] transition-all duration-500"
              >
                {/* Accent corner glow */}
                <div className="absolute -top-16 -right-16 w-40 h-40 bg-accent/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

                {/* Large index number */}
                <div className="absolute top-5 right-6 text-[5rem] leading-none font-bold text-accent/5 group-hover:text-accent/10 transition-colors duration-500 select-none pointer-events-none">
                  0{index + 1}
                </div>

                <div className="relative p-7 md:p-8">
                  {/* Problem (struck through) */}
                  <div className="flex items-start gap-2.5 mb-5">
                    <XCircle className="h-4 w-4 text-destructive/70 flex-shrink-0 mt-1" strokeWidth={2.2} />
                    <span className="text-sm text-muted-foreground line-through decoration-destructive/40">
                      {item.problem}
                    </span>
                  </div>

                  {/* Arrow divider */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="h-px flex-1 bg-gradient-to-r from-border via-accent/30 to-border" />
                    <ArrowRight className="h-4 w-4 text-accent" strokeWidth={2.5} />
                    <div className="h-px flex-1 bg-gradient-to-r from-border via-accent/30 to-border" />
                  </div>

                  {/* Solution */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center mt-0.5">
                      <CheckCircle2 className="h-4 w-4 text-accent" strokeWidth={2.5} />
                    </div>
                    <h3 className="text-foreground font-bold text-xl leading-tight">
                      {item.solution}
                    </h3>
                  </div>

                  {/* Supporting detail */}
                  <p className="text-muted-foreground text-sm leading-relaxed pl-10">
                    {item.detail}
                  </p>
                </div>

                {/* Bottom accent bar */}
                <div className="h-1 w-full bg-gradient-to-r from-transparent via-accent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom tagline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 mt-14 pt-10 border-t border-border"
        >
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-accent rounded-full" />
            <span className="text-foreground font-semibold text-lg">No downtime</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-accent rounded-full" />
            <span className="text-foreground font-semibold text-lg">No disruption</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-accent rounded-full" />
            <span className="text-foreground font-semibold text-lg">Just clean vehicles</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
